const MODEL_VIEWER_FEATURE = { name: "model-viewer", version: "1.12" };
const WARM_MODEL_TIMEOUT = 3000;
const IDLE_FALLBACK_DELAY = 1500;

let modelViewerRequest = null;
const warmedModels = new Map();

const requestModelViewerFeature = () =>
  new Promise((resolve) => {
    if (!window.Shopify?.loadFeatures) {
      resolve(false);
      return;
    }
    window.Shopify.loadFeatures([{ ...MODEL_VIEWER_FEATURE, onLoad: (errors) => resolve(!errors) }]);
  });

const loadModelViewer = () => {
  if (window.customElements.get("model-viewer")) return Promise.resolve(true);
  if (!modelViewerRequest) {
    modelViewerRequest = requestModelViewerFeature().then((isLoaded) =>
      isLoaded ? window.customElements.whenDefined("model-viewer").then(() => true) : false
    );
  }
  return modelViewerRequest;
};

const runWhenIdle = (callback) => {
  if (window.requestIdleCallback) {
    window.requestIdleCallback(callback, { timeout: WARM_MODEL_TIMEOUT });
    return;
  }
  setTimeout(callback, IDLE_FALLBACK_DELAY);
};

const buildHiddenModelViewer = (modelUrl) => {
  const viewer = document.createElement("model-viewer");
  viewer.setAttribute("src", modelUrl);
  viewer.setAttribute("loading", "eager");
  viewer.setAttribute("reveal", "auto");
  viewer.setAttribute("interaction-prompt", "none");
  viewer.setAttribute("aria-hidden", "true");
  viewer.style.cssText =
    "position:fixed;left:-10000px;top:0;width:200px;height:200px;opacity:0;pointer-events:none;";
  return viewer;
};

const warmModel = (modelUrl) => {
  if (!modelUrl || warmedModels.has(modelUrl)) return;
  warmedModels.set(modelUrl, null);
  runWhenIdle(async () => {
    const isLoaded = await loadModelViewer();
    if (!isLoaded) return;
    const hiddenViewer = buildHiddenModelViewer(modelUrl);
    document.body.appendChild(hiddenViewer);
    warmedModels.set(modelUrl, hiddenViewer);
  });
};

const removeWarmModel = (modelUrl) => {
  warmedModels.get(modelUrl)?.remove();
  warmedModels.set(modelUrl, null);
};

const buildHeroModelViewer = (settings) => {
  const viewer = document.createElement("model-viewer");
  viewer.setAttribute("src", settings.model_url);
  viewer.setAttribute("loading", "eager");
  viewer.setAttribute("alt", settings.alt ?? "");
  if (settings.auto_rotate) {
    viewer.setAttribute("auto-rotate", "");
    viewer.setAttribute("auto-rotate-delay", "0");
    viewer.setAttribute("rotation-per-second", `${settings.rotation_speed ?? 18}deg`);
  }
  if (settings.camera_orbit) viewer.setAttribute("camera-orbit", settings.camera_orbit);
  if (settings.field_of_view) {
    viewer.setAttribute("field-of-view", `${settings.field_of_view}deg`);
    viewer.setAttribute("min-field-of-view", `${settings.field_of_view}deg`);
    viewer.setAttribute("max-field-of-view", `${settings.field_of_view}deg`);
  }
  viewer.setAttribute("interpolation-decay", "1");
  viewer.setAttribute("disable-zoom", "");
  viewer.setAttribute("disable-pan", "");
  viewer.setAttribute("disable-tap", "");
  viewer.setAttribute("interaction-prompt", "none");
  viewer.setAttribute("shadow-intensity", "0");
  viewer.setAttribute("exposure", String(settings.exposure ?? 0.95));
  viewer.setAttribute("environment-image", "neutral");
  return viewer;
};

const MINIMUM_ACCENT_CONTRAST = 4.5;
const LOW_SATURATION = 0.08;
const LIGHT_BACKGROUND_LUMINANCE = 0.3;
const DARKEN_STEP = 0.09;
const MAXIMUM_DARKEN_STEPS = 20;
const DARK_INK = [20, 22, 26];
const LIGHT_INK = [255, 255, 255];

const hexToChannels = (hex) => {
  const digits = hex.replace("#", "").trim();
  const fullDigits = digits.length === 3 ? [...digits].map((digit) => digit + digit).join("") : digits;
  return [0, 2, 4].map((start) => parseInt(fullDigits.slice(start, start + 2), 16));
};

const WHITE_BACKGROUND = [255, 255, 255];

const rgbToChannels = (rgb) => {
  const values = (rgb.match(/\d+(\.\d+)?/g) ?? []).map(Number);
  const isTransparent = values.length === 4 && values[3] === 0;
  return isTransparent || values.length < 3 ? WHITE_BACKGROUND : values.slice(0, 3);
};

const channelsToHex = (channels) =>
  `#${channels
    .map((channel) => Math.round(Math.min(255, Math.max(0, channel))).toString(16).padStart(2, "0"))
    .join("")}`;

const luminanceOf = (channels) => {
  const linear = (channel) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * linear(channels[0]) + 0.7152 * linear(channels[1]) + 0.0722 * linear(channels[2]);
};

const contrastBetween = (first, second) => {
  const [lighter, darker] = [luminanceOf(first), luminanceOf(second)].sort((a, b) => b - a);
  return (lighter + 0.05) / (darker + 0.05);
};

const saturationOf = (channels) => (Math.max(...channels) - Math.min(...channels)) / 255;

const findReadableAccent = (accentHex, backgroundChannels) => {
  let accent = hexToChannels(accentHex);
  if (contrastBetween(accent, backgroundChannels) >= MINIMUM_ACCENT_CONTRAST) return channelsToHex(accent);

  const isLightBackground = luminanceOf(backgroundChannels) > LIGHT_BACKGROUND_LUMINANCE;
  if (saturationOf(accent) < LOW_SATURATION) return channelsToHex(isLightBackground ? DARK_INK : LIGHT_INK);

  const target = isLightBackground ? [0, 0, 0] : LIGHT_INK;
  for (let step = 0; step <= MAXIMUM_DARKEN_STEPS; step++) {
    if (contrastBetween(accent, backgroundChannels) >= MINIMUM_ACCENT_CONTRAST) break;
    accent = accent.map((channel, index) => channel + (target[index] - channel) * DARKEN_STEP);
  }
  return channelsToHex(accent);
};

const applyCampaignAccent = ($el) => {
  const drawer = $el.closest("[data-sale]");
  if (!drawer || drawer.dataset.sale === "none") return;
  const campaignAccent = getComputedStyle(drawer).getPropertyValue("--cmp-c1").trim();
  if (!campaignAccent.startsWith("#")) return;
  const backgroundChannels = rgbToChannels(getComputedStyle(drawer).backgroundColor);
  $el.style.setProperty("--apex-hero-accent", findReadableAccent(campaignAccent, backgroundChannels));
};

const WAITING_FOR_MODEL_CLASS = "is-waiting-for-model";
const HAS_MODEL_CLASS = "has-model";
const MODEL_SHOWN_CLASS = "is-shown";
const MODEL_WAIT_LIMIT = 8000;

const initCartHero = ($el) => {
  const settings =
    utils.JSONParse($el.querySelector("[data-cart-hero-settings]")?.innerHTML) ?? {};
  const hasModel = Boolean(settings.model_url);
  let isModelMounted = false;

  if (hasModel && settings.preload_model) warmModel(settings.model_url);
  applyCampaignAccent($el);

  const showPoster = () => $el.classList.remove(WAITING_FOR_MODEL_CLASS);

  const mountModel = async () => {
    if (!hasModel || isModelMounted) return;
    isModelMounted = true;

    const giveUpTimer = setTimeout(showPoster, MODEL_WAIT_LIMIT);
    const isLoaded = await loadModelViewer();
    if (!isLoaded) {
      clearTimeout(giveUpTimer);
      showPoster();
      return;
    }

    const shot = $el.querySelector("[data-cart-hero-shot]");
    const viewer = buildHeroModelViewer(settings);

    viewer.addEventListener(
      "load",
      () => {
        clearTimeout(giveUpTimer);
        viewer.classList.add(MODEL_SHOWN_CLASS);
        $el.classList.add(HAS_MODEL_CLASS);
        removeWarmModel(settings.model_url);
      },
      { once: true }
    );
    viewer.addEventListener(
      "error",
      () => {
        clearTimeout(giveUpTimer);
        viewer.remove();
        showPoster();
      },
      { once: true }
    );
    shot?.appendChild(viewer);
  };

  return {
    isAdding: false,
    mountModel,
    async addToCart() {
      if (!settings.variant_id || this.isAdding) return;
      this.isAdding = true;
      try {
        const ladder = window._sections?.getBundleLadder?.();
        await _cart.add({
          items: [
            {
              id: settings.variant_id,
              quantity: 1,
              properties: ladder?.bundleProperties(settings.product_id) ?? {},
            },
          ],
        });
      } finally {
        this.isAdding = false;
      }
    },
  };
};

window._sections = window._sections || {};
window._sections["initCartHero"] = initCartHero;
